import React, { useEffect } from "react";
import styled from "styled-components";
import { NetworkStatus } from "apollo-client";
import produce from "immer";
import { Modal } from "@sonder/ui/source/styled/components/Modal";
import { Modal as ModalFragment } from "@sonder/ui/source/styled/fragments/Modal";
import {
  SearchForTasksVariables,
  useSearchForTasks,
} from "core/queries/SearchForTasks";
import { clearSearchTerm } from "search/actions";
import { SearchBar } from "search/components/SearchBar";
import { SearchModalContent } from "search/components/SearchModalContent";
import { SearchPanel } from "search/components/SearchPanel";
import { useSearchStore } from "search/store";
import { getSearchSorting, statusTextToArrayOfStatuses } from "search/utils";

const StyledModal = styled(Modal)`
  ${ModalFragment} {
    display: flex;
    flex-direction: column;
    flex: 1 1 75vw;
    height: 90vh;
    max-width: 74.143rem;
    padding: 0;
  }
`;

const NumberOfResults = styled.div`
  align-items: center;
  display: flex;
  height: 2.571rem;
  padding: 0 1.714rem;
`;

const ModalBodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: hidden;
  position: relative;
`;

const SearchModalContentWrapper = styled.div`
  display: flex;
  height: 100%;
  overflow-y: auto;
  padding: 0 1.714rem;
`;

const LeftSearchContentWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  width: 75%;
`;

interface SearchModalProps {
  cityId: string;
  closeModal: () => void;
  isOpen: boolean;
}

export const SearchModal = ({
  cityId,
  closeModal,
  isOpen,
}: SearchModalProps) => {
  const { state, dispatch } = useSearchStore();
  const { statusFilterText, startDate, endDate, serverSearchTerm } = state;
  const isServerSearchTermEmpty = serverSearchTerm.trim() === "";

  useEffect(() => {
    return () => {
      dispatch(clearSearchTerm());
    };
  }, []);

  const getSearchTasksVariables = ({
    cursor,
  }: {
    cursor: string;
  }): SearchForTasksVariables => {
    return {
      searchTerm: serverSearchTerm,
      filters: {
        task: {
          cityIds: [cityId as string],
          statuses: statusTextToArrayOfStatuses(statusFilterText),
          createdAtStart: startDate?.toISOString() ?? null,
          createdAtEnd: endDate?.toISOString() ?? null,
        },
      },
      pagination: {
        cursor,
        limit: 25,
      },
      sorting: getSearchSorting(state.sortByText),
    };
  };

  const {
    data: searchData,
    networkStatus,
    fetchMore,
  } = useSearchForTasks({
    variables: getSearchTasksVariables({ cursor: "0" }),
    skip: isServerSearchTermEmpty || !cityId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const numberOfSearchResults = searchData?.searchWithMetadata.total;
  const hasEmptySearchResults = numberOfSearchResults === 0;
  const hasSearchResults = numberOfSearchResults
    ? numberOfSearchResults > 0
    : false;

  const numberOfResultsOrNull =
    !isServerSearchTermEmpty &&
    !hasEmptySearchResults &&
    networkStatus !== NetworkStatus.loading &&
    networkStatus !== NetworkStatus.setVariables
      ? `${numberOfSearchResults} results`
      : null;

  const fetchMoreTasks = () => {
    fetchMore({
      variables: getSearchTasksVariables({
        cursor: (
          parseInt(searchData?.searchWithMetadata.cursor ?? "0", 10) + 1
        ).toString(),
      }),
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const moreData = produce(prev, draft => {
          draft.searchWithMetadata = {
            ...fetchMoreResult.searchWithMetadata,
            items: [
              ...prev.searchWithMetadata.items,
              ...fetchMoreResult.searchWithMetadata.items,
            ],
          };
        });

        return moreData;
      },
    });
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={() => {
        closeModal();
      }}
    >
      {() => {
        return (
          <>
            <SearchBar
              closeModal={() => {
                closeModal();
              }}
            />

            <ModalBodyWrapper>
              <NumberOfResults>{numberOfResultsOrNull}</NumberOfResults>

              <SearchModalContentWrapper>
                <LeftSearchContentWrapper id="modal-content">
                  <SearchModalContent
                    fetchMoreTasks={fetchMoreTasks}
                    hasEmptySearchResults={hasEmptySearchResults}
                    hasMore={!!searchData?.searchWithMetadata.hasMore}
                    hasSearchResults={hasSearchResults}
                    networkStatus={networkStatus}
                    searchData={searchData}
                  />
                </LeftSearchContentWrapper>
                <SearchPanel />
              </SearchModalContentWrapper>
            </ModalBodyWrapper>
          </>
        );
      }}
    </StyledModal>
  );
};
